import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Shield, Eye, Users as UsersIcon, ChevronDown, UserPlus, Building2, Check, Crown, Sparkles, LayoutGrid, ClipboardList } from "lucide-react";
import { useAuth, isAdmin, isSuperAdmin, isFamily, isRestricted, hasWeeklyWins, activeOrg } from "../contexts/AuthContext";
import { TIER_LABEL, tierOf } from "../lib/permissions";
import { toast } from "sonner";
import { api } from "../lib/api";

const EMBLEM_URL = "https://customer-assets-agu9un31.emergentagent.net/job_player-analytics-73/artifacts/4wc3ngyr_MBDG-Emblem-OnDark.png.webp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
/** Convert `/api/...` relative paths to absolute URLs so <img> tags load across environments. */
function absLogoUrl(url) {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${BACKEND_URL}${url}`;
}

// Inline SVG "blueprint" texture — gears, grid lines, dimension arrows, circuit
// paths. Encoded once as a data URL so the browser tiles/scales it into the bar
// background without a network request.
const BLUEPRINT_SVG = `
<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 96' preserveAspectRatio='xMidYMid slice'>
  <defs>
    <pattern id='grid' width='16' height='16' patternUnits='userSpaceOnUse'>
      <path d='M 16 0 L 0 0 0 16' fill='none' stroke='#121212' stroke-width='0.4' opacity='0.28'/>
    </pattern>
    <pattern id='grid-major' width='80' height='80' patternUnits='userSpaceOnUse'>
      <path d='M 80 0 L 0 0 0 80' fill='none' stroke='#121212' stroke-width='0.6' opacity='0.32'/>
    </pattern>
  </defs>
  <rect width='800' height='96' fill='url(#grid)'/>
  <rect width='800' height='96' fill='url(#grid-major)'/>
  <g fill='none' stroke='#121212' stroke-width='0.8' opacity='0.55'>
    <!-- Left gear -->
    <g transform='translate(60 48)'>
      <circle r='22'/>
      <circle r='14'/>
      <circle r='4'/>
      ${Array.from({ length: 12 }).map((_, i) => {
        const a = (i * Math.PI * 2) / 12;
        const x1 = Math.cos(a) * 22, y1 = Math.sin(a) * 22;
        const x2 = Math.cos(a) * 27, y2 = Math.sin(a) * 27;
        return `<line x1='${x1.toFixed(2)}' y1='${y1.toFixed(2)}' x2='${x2.toFixed(2)}' y2='${y2.toFixed(2)}'/>`;
      }).join("")}
    </g>
    <!-- Circuit trace top -->
    <path d='M 110 20 L 210 20 L 220 30 L 300 30 L 310 20 L 380 20'/>
    <path d='M 210 20 L 210 8'/>
    <path d='M 300 30 L 300 44'/>
    <circle cx='210' cy='8' r='2'/>
    <circle cx='300' cy='44' r='2'/>
    <!-- Dimension line -->
    <path d='M 110 78 L 380 78'/>
    <path d='M 110 74 L 110 82 M 380 74 L 380 82'/>
    <!-- Right gear -->
    <g transform='translate(720 48)'>
      <circle r='26'/>
      <circle r='16'/>
      <circle r='4'/>
      ${Array.from({ length: 14 }).map((_, i) => {
        const a = (i * Math.PI * 2) / 14;
        const x1 = Math.cos(a) * 26, y1 = Math.sin(a) * 26;
        const x2 = Math.cos(a) * 31, y2 = Math.sin(a) * 31;
        return `<line x1='${x1.toFixed(2)}' y1='${y1.toFixed(2)}' x2='${x2.toFixed(2)}' y2='${y2.toFixed(2)}'/>`;
      }).join("")}
    </g>
    <!-- Right circuit trace -->
    <path d='M 660 22 L 560 22 L 550 32 L 470 32 L 460 22 L 420 22'/>
    <path d='M 560 22 L 560 10'/>
    <circle cx='560' cy='10' r='2'/>
    <path d='M 660 78 L 420 78'/>
    <path d='M 660 74 L 660 82 M 420 74 L 420 82'/>
    <!-- Small dashed measurement -->
    <path d='M 150 55 L 260 55' stroke-dasharray='4 3'/>
    <path d='M 540 55 L 650 55' stroke-dasharray='4 3'/>
    <!-- Cross-hair mid-left -->
    <g transform='translate(160 48)'>
      <circle r='5'/>
      <path d='M -9 0 L 9 0 M 0 -9 L 0 9'/>
    </g>
    <g transform='translate(640 48)'>
      <circle r='5'/>
      <path d='M -9 0 L 9 0 M 0 -9 L 0 9'/>
    </g>
    <!-- Small annotation marks -->
    <path d='M 250 65 L 258 65 L 258 72'/>
    <path d='M 550 65 L 542 65 L 542 72'/>
  </g>
</svg>`;
const BLUEPRINT_DATA_URL = `url("data:image/svg+xml;utf8,${encodeURIComponent(BLUEPRINT_SVG)}")`;

export default function Navbar({ right, onAddPlayer }) {
  const loc = useLocation();
  const navigate = useNavigate();
  const { user, logout, switchOrg, refresh } = useAuth();
  const onDash = loc.pathname === "/";
  // Back link: player page → that player's dashboard (/home/:id) for everyone;
  // /home page → coach dashboard (admins) or player list (family/trainer).
  const familyUser = isFamily(user);
  const restrictedUser = isRestricted(user);
  const seg = loc.pathname.split("/");
  const onPlayerPage = seg[1] === "player" && seg[2];
  const onHomePage = seg[1] === "home";
  const onTiqPage = ["tactical-iq", "technical-skill", "player-archetypes", "physical-tools", "character-dna"].includes(seg[1]);
  const tiqName = { "technical-skill": "Technical Skill", "player-archetypes": "Player Archetypes", "physical-tools": "Physical Tools", "character-dna": "Character DNA" }[seg[1]] || "Tactical IQ";
  const weeklyWinsAddOn = restrictedUser && hasWeeklyWins(user);
  const backTo = onPlayerPage ? `/home/${seg[2]}` : onTiqPage ? (seg[2] ? `/${seg[1]}` : (restrictedUser ? "/my-players" : "/coach")) : (restrictedUser ? "/my-players" : "/");
  const backLabel = onPlayerPage ? "Player Dashboard" : onTiqPage ? (seg[2] ? tiqName : (restrictedUser ? "Players" : "Command Center")) : (restrictedUser ? (onHomePage ? "Players" : "Home") : "Dashboard");
  void familyUser;
  const [menuOpen, setMenuOpen] = useState(false);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const orgMenuRef = useRef(null);
  const active = activeOrg(user);

  useEffect(() => {
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (orgMenuRef.current && !orgMenuRef.current.contains(e.target)) setOrgMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const doSwitch = async (orgId) => {
    if (orgId === user?.active_org_id) { setOrgMenuOpen(false); return; }
    try {
      const me = await switchOrg(orgId);
      const newOrg = (me.orgs || []).find(o => o.id === orgId);
      toast.success(`Switched to ${newOrg?.name || "org"}`);
      setOrgMenuOpen(false);
      // Kick to dashboard so cached data reloads for the new org context
      navigate("/", { replace: true });
      // Force a hard refresh of any component state built on the old org
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Switch failed");
    }
  };

  const canSwitchOrgs = user && (user.orgs || []).length > 1;

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0a0a0a]/80 backdrop-blur-xl" style={{ boxShadow: "0 1px 0 rgba(255,255,255,0.04), 0 10px 30px -20px rgba(0,0,0,0.8)" }}>
        <div className="absolute inset-x-0 bottom-0 h-px pointer-events-none" style={{ background: "linear-gradient(90deg, transparent, var(--org-accent) 30%, #3aa0ff 70%, transparent)", opacity: 0.45 }} />
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-14 sm:h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center group shrink-0" data-testid="nav-logo">
            <img src={EMBLEM_URL} alt="Maximize Basketball Development Group"
              className="h-9 sm:h-11 w-auto group-hover:scale-105" style={{ transition: "transform 0.2s ease" }} />
          </Link>

          <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
            {isSuperAdmin(user) && loc.pathname !== "/coach" && (
              <Link to="/coach" title="Coaches Dashboard"
                className="flex items-center gap-1.5 shrink-0 px-2.5 h-8 border border-[#3aa0ff]/40 text-[#3aa0ff] text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#3aa0ff]/10"
                style={{ transition: "background-color 0.2s ease" }} data-testid="nav-coach-dashboard">
                <ClipboardList className="w-3.5 h-3.5" /><span className="hidden sm:inline">Coaches Dashboard</span>
              </Link>
            )}
            {weeklyWinsAddOn && !loc.pathname.startsWith("/weekly-wins") && (
              <Link to="/weekly-wins" title="Weekly Wins"
                className="flex items-center gap-1.5 shrink-0 px-2.5 h-8 border border-[var(--org-accent)]/40 text-[var(--org-accent)] text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[var(--org-accent)]/10"
                style={{ transition: "background-color 0.2s ease" }} data-testid="nav-weekly-wins">
                <Sparkles className="w-3.5 h-3.5" /><span className="hidden sm:inline">Weekly Wins</span>
              </Link>
            )}
            {isAdmin(user) && !onDash && (
              <Link to="/" title="Player Tracker"
                className="flex items-center gap-1.5 shrink-0 px-2.5 h-8 border border-[var(--org-accent)]/40 text-[var(--org-accent)] text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[var(--org-accent)]/10"
                style={{ transition: "background-color 0.2s ease" }} data-testid="nav-player-tracker">
                <LayoutGrid className="w-3.5 h-3.5" /><span className="hidden sm:inline">Player Tracker</span>
              </Link>
            )}
            {onDash ? null : (
              <Link to={backTo} className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] hover:text-[var(--org-accent)] shrink-0"
                style={{ transition: "color 0.2s ease" }} data-testid="nav-back-dashboard">
                <span className="hidden sm:inline">← {backLabel}</span>
                <span className="sm:hidden text-lg">←</span>
              </Link>
            )}
            {right}
            {/* Org switcher — visible when the user has more than one org */}
            {canSwitchOrgs && (
              <div className="relative shrink-0" ref={orgMenuRef}>
                <button
                  onClick={() => setOrgMenuOpen(o => !o)}
                  className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 h-9 border border-white/10 hover:border-[var(--org-accent)]"
                  style={{ transition: "border-color 0.15s ease" }}
                  data-testid="org-switcher-btn"
                >
                  <Building2 className="w-3.5 h-3.5 text-[var(--org-accent)] shrink-0" />
                  <span className="hidden sm:inline text-xs text-white max-w-[180px] truncate">{active?.name || "Choose org"}</span>
                  <ChevronDown className="w-3 h-3 text-[#757575] shrink-0" />
                </button>
                {orgMenuOpen && (
                  <div className="absolute right-0 mt-1 min-w-[240px] border border-white/10 bg-[#1c1c1c] shadow-xl z-50" data-testid="org-switcher-menu">
                    <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-[#757575] border-b border-white/10">
                      Switch Organization
                    </div>
                    {(user.orgs || []).map(o => (
                      <button key={o.id}
                        onClick={() => doSwitch(o.id)}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-white hover:bg-white/5 border-b border-white/5 text-left"
                        data-testid={`org-switcher-item-${o.id}`}
                      >
                        <span className="inline-block w-2.5 h-2.5" style={{ background: o.accent_color || "#d8b854" }} />
                        <span className="flex-1 truncate">{o.name}</span>
                        {o.id === user.active_org_id && <Check className="w-3.5 h-3.5 text-[var(--org-accent)]" />}
                      </button>
                    ))}
                    {isSuperAdmin(user) && (
                      <Link to="/orgs" onClick={() => setOrgMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-xs text-[var(--org-accent)] hover:bg-white/5 border-t border-white/10"
                        data-testid="org-manage-link">
                        <Crown className="w-3.5 h-3.5" /> Manage Organizations
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )}
            {user && user.role && (
              <div className="relative shrink-0" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen(o => !o)}
                  className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 h-9 border border-white/10 hover:border-[var(--org-accent)]"
                  style={{ transition: "border-color 0.15s ease" }}
                  data-testid="user-menu-btn"
                >
                  {isAdmin(user)
                    ? <Shield className="w-3.5 h-3.5 text-[var(--org-accent)] shrink-0" />
                    : <Eye className="w-3.5 h-3.5 text-[#60a5fa] shrink-0" />}
                  <span className="hidden sm:inline text-xs text-white max-w-[160px] truncate">{user.name || user.email}</span>
                  <ChevronDown className="w-3 h-3 text-[#757575] shrink-0" />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-1 min-w-[220px] border border-white/10 bg-[#1c1c1c] shadow-xl z-50" data-testid="user-menu">
                    <div className="px-4 py-3 border-b border-white/10">
                      <div className="text-[10px] uppercase tracking-widest text-[#757575]">Signed in as</div>
                      <div className="text-sm text-white truncate">{user.email}</div>
                      <NameEditor user={user} onSaved={refresh} />
                      <div className="mt-1 text-[9px] uppercase tracking-widest text-[var(--org-accent)]">
                        {isAdmin(user) ? "Admin · Full access" : `${TIER_LABEL[tierOf(user)] || "Viewer"} · Read-only${weeklyWinsAddOn ? " · Weekly Wins" : ""}`}
                      </div>
                    </div>
                    {isAdmin(user) && onAddPlayer && (
                      <button
                        onClick={() => { setMenuOpen(false); onAddPlayer(); }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-white hover:bg-white/5 border-b border-white/5 text-left"
                        data-testid="nav-add-player"
                      >
                        <UserPlus className="w-3.5 h-3.5" /> Add Player
                      </button>
                    )}
                    {isAdmin(user) && (
                      <Link to="/users" onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-xs text-white hover:bg-white/5 border-b border-white/5"
                        data-testid="nav-users">
                        <UsersIcon className="w-3.5 h-3.5" /> {active ? `Manage ${active.name} Members` : "Manage Members"}
                      </Link>
                    )}
                    {isSuperAdmin(user) && (
                      <Link to="/orgs" onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-xs text-[var(--org-accent)] hover:bg-white/5 border-b border-white/5"
                        data-testid="nav-orgs">
                        <Crown className="w-3.5 h-3.5" /> Manage Organizations
                      </Link>
                    )}
                    {isAdmin(user) && (
                      <Link to="/synergy-demo" onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-xs text-[var(--org-accent)] hover:bg-white/5 border-b border-white/5"
                        data-testid="nav-synergy-demo">
                        <Sparkles className="w-3.5 h-3.5" /> Synergy Demo (NCAA)
                      </Link>
                    )}
                    <button onClick={doLogout}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-red-400 hover:bg-red-500/10 text-left"
                      data-testid="nav-logout">
                      <LogOut className="w-3.5 h-3.5" /> Sign out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>
      {/* Gold wordmark bar with blueprint texture. Wordmark reflects the active org
          so branding flips instantly on switch. */}
      <div
        className="border-b border-black/20 relative overflow-hidden"
        style={{ background: active?.accent_color || "#d8b854" }}
        data-testid="brand-bar"
      >
        {/* Blueprint SVG background (gears, grid, dimension lines, crosshairs). Feathered
            toward the center so the wordmark stays readable. */}
        <div
          className="absolute inset-0 pointer-events-none opacity-45"
          aria-hidden="true"
          style={{
            backgroundImage: BLUEPRINT_DATA_URL,
            backgroundRepeat: "repeat-x",
            backgroundSize: "auto 100%",
            maskImage: "linear-gradient(to right, black 0%, black 22%, transparent 40%, transparent 60%, black 78%, black 100%)",
            WebkitMaskImage: "linear-gradient(to right, black 0%, black 22%, transparent 40%, transparent 60%, black 78%, black 100%)",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-2 sm:py-2.5 flex items-center justify-center gap-2 sm:gap-3">
          {active?.logo_url && (
            <img
              src={absLogoUrl(active.logo_url)}
              alt={active.name}
              className="h-5 sm:h-6 md:h-7 w-auto object-contain shrink-0"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              data-testid="brand-bar-logo"
            />
          )}
          <span
            className="font-heading text-[10px] sm:text-sm md:text-lg lg:text-xl uppercase tracking-[0.15em] sm:tracking-[0.25em] md:tracking-[0.35em] text-[#121212] text-center truncate"
            data-testid="brand-bar-name"
          >
            {active?.name ? `${active.name} · Analytics & Development` : "Maximize Analytics and Development Database"}
          </span>
        </div>
      </div>
    </>
  );
}

/** Inline "Your name" editor in the account menu — the first name is used in email greetings (Game Day digest). */
function NameEditor({ user, onSaved }) {
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(user.name || "");
  const [saving, setSaving] = React.useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (val.trim().length < 2) return;
    setSaving(true);
    try { await api.updateMe({ name: val.trim() }); await onSaved?.(); setEditing(false); toast.success("Name updated"); }
    catch (err) { toast.error("Could not update name", { description: err?.response?.data?.detail || err.message }); }
    finally { setSaving(false); }
  };
  if (!editing) {
    return (
      <button type="button" onClick={() => { setVal(user.name || ""); setEditing(true); }} className="mt-1.5 flex items-center gap-1.5 text-[11px] text-[#a0a0a0] hover:text-white" data-testid="nav-edit-name">
        <span className="text-white">{user.name || "Add your name"}</span><span className="text-[9px] uppercase tracking-widest text-[var(--org-accent)]">· Edit name</span>
      </button>
    );
  }
  return (
    <form onSubmit={save} className="mt-2 flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
      <input autoFocus value={val} onChange={e => setVal(e.target.value)} placeholder="First Last" maxLength={80}
        className="flex-1 min-w-0 bg-[#0f0f0f] border border-white/15 px-2 py-1 text-xs text-white focus:outline-none focus:border-[var(--org-accent)]" data-testid="nav-name-input" />
      <button type="submit" disabled={saving || val.trim().length < 2} className="px-2 py-1 bg-[var(--org-accent)] text-[#121212] text-[9px] uppercase tracking-widest font-bold disabled:opacity-40" data-testid="nav-name-save">Save</button>
      <button type="button" onClick={() => setEditing(false)} className="px-1.5 py-1 text-[9px] uppercase tracking-widest text-[#a0a0a0] hover:text-white" data-testid="nav-name-cancel">✕</button>
    </form>
  );
}
